/**
 * Single sign-on (piece 21; decision 0352): an OpenID Connect adapter behind
 * the `AuthProvider` seam, configured on Settings › Integrations › Single
 * sign-on. The rules here are pure, so the API, the Settings page and the
 * Login Screen read one key and one wording each.
 */
export const SSO_ENABLED_KEY = 'auth.ssoEnabled';
export const SSO_PROVIDER_NAME_KEY = 'auth.ssoProviderName';
export const SSO_ISSUER_URL_KEY = 'auth.ssoIssuerUrl';
export const SSO_CLIENT_ID_KEY = 'auth.ssoClientId';
/** Held sealed in the KeyProvider envelope, like the Google key of decision 0319. */
export const SSO_CLIENT_SECRET_KEY = 'auth.ssoClientSecret';
export const SSO_EMAIL_CLAIM_KEY = 'auth.ssoEmailClaim';
/** 🔨 The overlap for turning single sign-on off: passwords accepted beside the provider while people set one (piece 21). */
export const SSO_PASSWORD_OVERLAP_KEY = 'auth.ssoPasswordOverlap';

export const DEFAULT_SSO_PROVIDER_NAME = 'Microsoft';
export const DEFAULT_SSO_EMAIL_CLAIM = 'email';

/** The rows the seed writes once, at the defaults the piece names; the issuer, the client id and the secret start unset. */
export const SSO_SETTINGS: readonly { key: string; value: string | boolean }[] = [
  { key: SSO_ENABLED_KEY, value: false },
  { key: SSO_PROVIDER_NAME_KEY, value: DEFAULT_SSO_PROVIDER_NAME },
  { key: SSO_EMAIL_CLAIM_KEY, value: DEFAULT_SSO_EMAIL_CLAIM },
  { key: SSO_PASSWORD_OVERLAP_KEY, value: false },
];

/** The seven keys the Settings page reads and writes. */
export const SSO_SETTING_KEYS: readonly string[] = [
  SSO_ENABLED_KEY, SSO_PROVIDER_NAME_KEY, SSO_ISSUER_URL_KEY, SSO_CLIENT_ID_KEY, SSO_CLIENT_SECRET_KEY, SSO_EMAIL_CLAIM_KEY, SSO_PASSWORD_OVERLAP_KEY,
];

/** What the Login Screen learns before a session exists: whether to draw the button, and what to call it. */
export interface SsoStatus {
  enabled: boolean;
  providerName: string;
}

/** Where the screen keeps the state between following the authorization URL and the provider's return. */
export const SSO_STATE_STORAGE_KEY = 'bms2.sso.state';

/** The flow's own lifetime in the API's memory, beside the challenges of decision 0325. */
export const SSO_FLOW_MINUTES = 10;

/** 🔨 The redirect the provider is registered with is the Login Screen itself: the flow begins and ends there (decision 0352). */
export function ssoRedirectUri(appBaseUrl: string): string {
  return `${appBaseUrl.replace(/\/+$/, '')}/login`;
}

/**
 * The issuer as OpenID Connect discovery expects it: https, no query, no
 * fragment, and no trailing slash so `<issuer>/.well-known/openid-configuration`
 * composes cleanly. Null where the text is not an issuer at all.
 */
export function normalizeIssuerUrl(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || url.search || url.hash || !url.hostname) return null;
  return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
}

export interface SsoDraft {
  enabled: boolean;
  providerName: string;
  issuerUrl: string;
  clientId: string;
  /** Empty means the held secret stays; typing one replaces it. */
  clientSecret: string;
  /** Whether a secret is already held, so the switch can be turned on without typing one again. */
  secretHeld: boolean;
  emailClaim: string;
  passwordOverlap: boolean;
}

/** What stops the Settings page saving: the switch cannot go on over an incomplete configuration. */
export function ssoProblems(draft: SsoDraft): string[] {
  const problems: string[] = [];
  if (draft.issuerUrl.trim() && normalizeIssuerUrl(draft.issuerUrl) === null) problems.push('issuerUrl');
  if (!/^[A-Za-z_][A-Za-z0-9_.-]*$/.test(draft.emailClaim.trim())) problems.push('emailClaim');
  if (draft.enabled) {
    if (!draft.issuerUrl.trim()) problems.push('issuerUrl');
    if (!draft.clientId.trim()) problems.push('clientId');
    if (!draft.clientSecret.trim() && !draft.secretHeld) problems.push('clientSecret');
    if (!draft.providerName.trim()) problems.push('providerName');
  }
  return [...new Set(problems)];
}

/**
 * The email claim as a login email: lower-cased and trimmed, or null where
 * the claim is absent or not an address. 🔨 A provider's identifier that is
 * not an address matches nothing; no login is created on a provider's say-so.
 */
export function loginEmailFromClaim(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const email = value.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

/** The Login Screen's own wording for an identity that names no login here, and for the account that never goes to the provider. */
export const SSO_NO_LOGIN_COPY = 'No login matches that identity. Contact your Company’s own Admin for an invite.';
export const SSO_BREAK_GLASS_COPY = 'The break-glass administrator signs in with its password and code, never through the provider.';
export const SSO_PORTAL_COPY = 'A portal login signs in with its password; single sign-on is for the Company’s own logins.';
export const SSO_LAPSED_COPY = 'The sign-in lapsed; press the button again.';
/** 🔒 Decision 1503: a token whose `email_verified` claim is false names an address the provider has not confirmed, and no login is matched on it. */
export const SSO_UNVERIFIED_COPY = 'The provider has not verified that email address, so nothing was signed in; verify the address with the provider, then try again.';
export const SSO_REFUSED_COPY = 'The provider’s answer could not be verified. Nothing was signed in.';

/** The password form's refusal while single sign-on is on and the overlap is off (decision 0352). */
export function ssoPasswordRefusal(providerName: string): string {
  return `Sign in with ${providerName}; the password is not accepted while single sign-on is on.`;
}
